import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = ["Refúgio na serra,", "silêncio de", "montanha."];
const ease = [0.22, 1, 0.36, 1];

const Seal = () => (
  <div className="relative h-28 w-28 sm:h-36 sm:w-36">
    <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0 h-full w-full">
      <defs>
        <path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
      </defs>
      <text fill="#faf7f2" fontSize="15" fontWeight="700" letterSpacing="5.2">
        <textPath href="#circ">RESERVA MANTIQUEIRA • SERRA NEGRA • SP •</textPath>
      </text>
    </svg>
    <img src="/img/logo-white.png" alt="" className="absolute inset-[26%] h-[48%] w-[48%] object-contain" />
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative h-[100svh] min-h-[640px] overflow-hidden bg-[#163323]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src="/img/vista-6.jpg"
          alt="Piscina aquecida da Casa Vista com vista para a serra"
          className="h-full w-full object-cover"
          initial={{ clipPath: "inset(12% 10% 12% 10%)", filter: "brightness(0.6)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", filter: "brightness(1)" }}
          transition={{ duration: 1.8, ease }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d2016] via-[#0d2016]/45 to-[#0d2016]/30" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-14 sm:px-8 sm:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8, ease }}
          className="mb-6 text-xs font-bold uppercase tracking-[0.35em] text-[#e9c9a8]"
        >
          Serra Negra · São Paulo · Chalés de alto padrão
        </motion.p>
        <h1 data-testid="hero-title" className="max-w-5xl text-5xl font-bold uppercase leading-[0.95] tracking-[-0.03em] text-[#faf7f2] sm:text-6xl lg:text-[6.5rem]">
          {LINES.map((l, i) => (
            <span key={l} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#e9c9a8]" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.5 + i * 0.14, duration: 1.1, ease }}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }} className="max-w-xl">
            <p className="max-w-md text-base leading-relaxed text-[#f1ece3] md:text-lg">
              Casa Vista e Cabana Boutique: dois chalés com piscina aquecida, lareira e adega climatizada, cercados pelo verde da serra.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                data-testid="hero-reserve-btn"
                onClick={() => scrollToId("reservar")}
                className="group flex items-center gap-2 rounded-full bg-[#c86d4b] px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#a4532f]"
              >
                Reservar agora
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
              </button>
              <button
                data-testid="hero-chalets-btn"
                onClick={() => scrollToId("chales")}
                className="rounded-full border border-[#faf7f2]/50 px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#faf7f2] transition-colors duration-300 hover:bg-[#faf7f2] hover:text-[#163323]"
              >
                Conhecer os chalés
              </button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.3, duration: 1, ease }} className="self-end">
            <Seal />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
